import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { useEffect, useMemo, useRef, useState } from "react";
import useWindowDimensions from "../helpers/useWindowDimensions";

export default function Map({ isLoaded = false, center, locations, educationAddress, onClick, heightOverride = undefined, disableDefaultUI = false, routePolyline = null }) {
  const [markers, setMarkers] = useState([]);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const { width, height } = useWindowDimensions();

  function largeHeight() {
    return width < 1024 ? height * 0.4 : "100%";
  }

  function decodePolyline(encoded) {
    if (!encoded || typeof encoded !== "string") return [];
    let index = 0;
    const len = encoded.length;
    const path = [];
    let lat = 0;
    let lng = 0;

    while (index < len) {
      let result = 0;
      let shift = 0;
      let b;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
      lat += dlat;

      result = 0;
      shift = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
      lng += dlng;

      path.push({ lat: lat / 1e5, lng: lng / 1e5 });
    }
    return path;
  }

  const polyPath = useMemo(() => decodePolyline(routePolyline), [routePolyline]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!mapInstanceRef.current) return;
    if (!polyPath || polyPath.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    polyPath.forEach((p) => bounds.extend(p));
    mapInstanceRef.current.fitBounds(bounds, 100);
  }, [isLoaded, polyPath]);

  const containerStyle = {
    flexBasis: width < 1024 ? width - 20 : Math.max(width * 0.4 || 600),
    flexGrow: 1,
    flexShrink: 0,
    marginRight: 0,
    height: heightOverride || largeHeight(),
    maxHeight: "100%",
  };

  function getMarkerIcon(match) {
    const image = {
      url: "",
      scaledSize: new window.google.maps.Size(32, 32),
    };
    const baseURL = `https://app.bplaced.co.uk/markers/`;

    if (match) {
      if (match.gender === 0 || match.ageInMonths === 0 || match.available === 0) {
        image.url = `${baseURL}/redMarker.png`;
        return image;
      }

      if (match.filters < 80) {
        image.url = `${baseURL}/orangeMarker.png`;
        return image;
      }

      image.url = `${baseURL}/greenMarker.png`;
      return image;
    } else {
      console.log(`WARN: Unknown match for location`);
      image.url = `${baseURL}/greenMarker.png`;
      return image;
    }
  }

  useEffect(() => {
    const locationMarkers = locations.map((location) => {
      return (
        <Marker
          key={location.key}
          position={location.position}
          clickable={true}
          icon={getMarkerIcon(location.match)}
          title={location.name}
          onClick={() => {
            if (typeof onClick === "function") {
              onClick(location);
            }
          }}
        />
      );
    });

    // Add education address marker if available
    const educationMarker = educationAddress && educationAddress.lat && educationAddress.lng ? (
      <Marker
        key="education-address"
        position={{ lat: educationAddress.lat, lng: educationAddress.lng }}
        clickable={false}
        title={`Education: ${educationAddress.label}`}
        icon={{
          url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
          scaledSize: new window.google.maps.Size(32, 32),
        }}
      />
    ) : null;

    setMarkers([...locationMarkers, educationMarker].filter(Boolean));
  }, [locations, educationAddress]);

  if (isLoaded) {
    return (
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={11}
        options={{ disableDefaultUI: disableDefaultUI }}
        ref={mapRef}
        onLoad={(map) => {
          mapInstanceRef.current = map;
        }}
      >
        {routePolyline ? (
          <Polyline
            path={polyPath}
            options={{
              strokeColor: "#4285f4",
              strokeOpacity: 0.9,
              strokeWeight: 5,
            }}
          />
        ) : null}
        {markers}
      </GoogleMap>
    );
  }
}
